import { useEffect, useMemo, useState } from 'react';
import { TagRes, MipClassifierRes } from '@dealstack/shared/lib/generated';
import { isValidTagName } from '@dealstack/shared/lib/common/business/tagName';
import {
	useAttachClassifiersToTag,
	useCreateTag,
	useDetachClassifiersFromTag,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useTags.ts';

/** Sentinel prefix marking a select value that is a not-yet-created tag name. */
const NEW_PREFIX = 'new:';

interface Props {
	opened: boolean;
	cohort: MipClassifierRes | null;
	mipId: string;
	allTags: TagRes[];
	onSaved: () => void;
}

type Option = { value: string; label: string };

/**
 * State + save orchestration for the per-cohort "Manage tags" drawer. The select
 * holds existing tag ids plus `new:<name>` sentinels for tags the user typed to
 * create. On save we create the new tags (with this cohort attached), attach the
 * cohort to newly-selected existing tags, and detach it from removed ones.
 */
export const useManageCohortTagsDrawer = ({ opened, cohort, mipId, allTags, onSaved }: Props) => {
	const { mutateAsync: createTag, isPending: isCreating } = useCreateTag({ mipId });
	const { mutateAsync: attach, isPending: isAttaching } = useAttachClassifiersToTag({ mipId });
	const { mutateAsync: detach, isPending: isDetaching } = useDetachClassifiersFromTag({ mipId });

	const currentTagIds = useMemo(
		() =>
			cohort
				? allTags
						.filter((t) => t.classifiers.some((c) => c.id === cohort.id))
						.map((t) => t.id)
				: [],
		[allTags, cohort]
	);

	const [selected, setSelected] = useState<string[]>(currentTagIds);
	const [createdOptions, setCreatedOptions] = useState<Option[]>([]);

	useEffect(() => {
		if (opened) {
			setSelected(currentTagIds);
			setCreatedOptions([]);
		}
	}, [opened, currentTagIds]);

	const options = useMemo<Option[]>(
		() => [...allTags.map((t) => ({ value: t.id, label: t.name })), ...createdOptions],
		[allTags, createdOptions]
	);

	const handleCreate = (query: string): Option => {
		const trimmed = query.trim();
		const item: Option = { value: `${NEW_PREFIX}${trimmed}`, label: trimmed };
		setCreatedOptions((prev) => [...prev, item]);
		return item;
	};

	const shouldCreate = (query: string, data: Option[]): boolean => {
		const trimmed = query.trim();
		return (
			isValidTagName(trimmed) &&
			!data.some((item) => item.label.toLowerCase() === trimmed.toLowerCase())
		);
	};

	const selectedExistingIds = selected.filter((v) => !v.startsWith(NEW_PREFIX));
	const hasNewTag = selected.some((v) => v.startsWith(NEW_PREFIX));
	const sameAsCurrent =
		selectedExistingIds.length === currentTagIds.length &&
		selectedExistingIds.every((id) => currentTagIds.includes(id));
	const isDirty = hasNewTag || !sameAsCurrent;

	const handleSave = async () => {
		if (!cohort || !isDirty) {
			return;
		}
		const cohortId = cohort.id;
		const newNames = selected
			.filter((v) => v.startsWith(NEW_PREFIX))
			.map((v) => v.slice(NEW_PREFIX.length));
		const toAttach = selectedExistingIds.filter((id) => !currentTagIds.includes(id));
		const toDetach = currentTagIds.filter((id) => !selectedExistingIds.includes(id));

		try {
			await Promise.all([
				...newNames.map((name) => createTag({ name, mipClassifierIds: [cohortId] })),
				...toAttach.map((tagId) =>
					attach({ tagId, body: { mipClassifierIds: [cohortId] } })
				),
				...toDetach.map((tagId) =>
					detach({ tagId, body: { mipClassifierIds: [cohortId] } })
				),
			]);
			onSaved();
		} catch {
			// Mutation onError handlers already surface a toast.
		}
	};

	return {
		options,
		selected,
		setSelected,
		handleCreate,
		shouldCreate,
		handleSave,
		isDirty,
		isSaving: isCreating || isAttaching || isDetaching,
	};
};
